<template>
    <div class="image-bot-response-container">
        <!-- Mode Selector -->
        <div class="mode-selector">
            <div class="mode-switch-container">
                <span 
                    class="mode-label" 
                    :class="{ active: currentMode === 'static' }"
                    @click="setMode('static')"
                >
                    <span class="material-icons">photo_camera</span>
                    Static
                </span>
                <a-switch 
                    v-model="isDynamicMode" 
                    @change="onModeChanged"
                    class="mode-switch"
                />
                <span 
                    class="mode-label" 
                    :class="{ active: currentMode === 'dynamic' }"
                    @click="setMode('dynamic')"
                >
                    <span class="material-icons">code</span>
                    Dynamic
                </span>
            </div>
        </div>

        <!-- Static Mode - File Upload -->
        <div v-if="currentMode === 'static'" class="image-bot-response" :class="{ empty: !defaultImageUrl }">
            <div class="placeholder" v-if="!defaultImageUrl && !selectedImage" @click="openSelectImage">
                <span class="material-icons"> photo_camera </span>
                <span>{{ $t("chatbotv2.dragAndDropBrowse") }}</span>
            </div>
            <div v-else class="position-relative" @mouseover="hovered = true" @mouseleave="hovered = false">
                <img class="preview" :src="defaultImageUrl" v-if="defaultImageUrl && !uploading" />
                <img class="preview" :src="selectedImageSrc" v-else-if="selectedImageSrc" />
                <div class="uploading-banner" v-if="uploading">
                    <a-progress
                        type="circle"
                        :stroke-color="{
                            '0%': '#108ee9',
                            '100%': '#87d068',
                        }"
                        :percent="uploadProgress"
                    />
                    <span>{{ $t("chatbotv2.imageUploading") }} ...</span>
                </div>
                <div class="update-banner" v-if="defaultImageUrl && !uploading && hovered" @click="openSelectImage">
                    <span class="material-icons"> photo_camera </span>
                    <span>{{ $t("chatbotv2.clickToChooseNewImage") }}</span>
                    <span
                        >{{ $t("chatbotv2.or") }}
                        <span class="remove" @click="removeImage">{{ $t("chatbotv2.removeTheExistingOne") }}</span></span
                    >
                </div>
            </div>
        </div>

        <!-- Dynamic Mode - TiptapEditor -->
        <div v-if="currentMode === 'dynamic'" class="dynamic-image-url">
            <label class="url-label">{{ $t("chatbotv2.imageUrl") }}:</label>
            <TiptapEditor
                :placeholder="$t('chatbotv2.enterImageUrlHere')"
                :show-attributes="true"
                :show-emoji="false"
                v-model="dynamicImageUrl"
                :preview-mode="false"
                :whatsapp-mode="whatsappMode"
                :allowed-attributes="allowedAttributes"
            />
        </div>
    </div>
</template>

<script>
import log2 from "../../../../../../../utils/log2";
import { uploadAsset } from "../../../../../../../api/assetUpload";
import mimeTypes from "../../../../../../../components/util/mimeTypes";
import { getExtensionFromFileName } from "../../../../../../../utils/utils";
import axios from "axios";
import { mapState } from "vuex";
import * as _ from "lodash";
import TiptapEditor from "../../common/TiptapEditor";

const log = log2("ImageBotResponse");
export default {
    name: "ImageBotResponse",
    components: { TiptapEditor },
    props: ["defaultImageUrl", "defaultAssetId", "onUpdated", "whatsappMode", "allowedAttributes", "defaultMode"],
    data() {
        return {
            selectedImage: null,
            selectedImageSrc: null,
            uploading: false,
            uploadProgress: 0,
            hovered: false,
            currentMode: this.defaultMode || "static",
            dynamicImageUrl: this.defaultMode === "dynamic" ? this.defaultImageUrl || "" : "",
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
        }),
        isDynamicMode: {
            get() {
                return this.currentMode === 'dynamic';
            },
            set(value) {
                this.currentMode = value ? 'dynamic' : 'static';
            }
        }
    },
    methods: {
        openSelectImage() {
            let fileEl = document.createElement("input");
            fileEl.setAttribute("type", "file");
            fileEl.setAttribute("accept", "image/*");

            fileEl.onchange = async () => {
                let file = fileEl.files[0];
                const fileType = getExtensionFromFileName(file.name);
                if (file) {
                    this.selectedImage = file;
                    this.selectedImageSrc = URL.createObjectURL(file);
                    this.uploading = true;
                    let path = `${this.site._id}/chatbot-file/`;

                    let { data } = await uploadAsset({
                        path,
                        mime: file.type,
                        name: file.name,
                        ext: mimeTypes[file.type] || fileType || "supsis",
                        size: file.size,
                        type: "chatbot",
                    });
                    log("file", { file, data });
                    if (file.size <= 5 * 1024 * 1024) {
                        axios({
                            method: data.method,
                            url: data.url,
                            data: file,
                            headers: {
                                "Content-Type": file.type,
                            },
                            onUploadProgress: (p) => {
                                const percent = Math.round((p.loaded * 100) / p.total);
                                this.uploadProgress = percent;
                            },
                        })
                            .then((res) => {
                                this.$message.success({
                                    content: `Image uploaded successfully`,
                                    duration: 2,
                                    key: "uploading",
                                });
                                this.onUpdated({
                                    assetId: data.asset._id,
                                    imageUrl: data.asset.v2Format && data.asset.cdnUrl
                                        ? data.asset.cdnUrl
                                        : `https://static.supsis.live/${data.asset.path}${data.filename}`,
                                });
                            })
                            .catch((err) => {
                                this.$message.error({
                                    content: "Image upload failed",
                                    duration: 2,
                                    key: "uploading",
                                });
                            })
                            .finally(() => {
                                this.uploading = false;
                                this.selectedImage = null;
                                this.selectedImageSrc = null;
                                this.hovered = false;
                            });
                    } else {
                        log.warn("input is not suitable for send");
                        this.$message.error({
                            content: this.$t("message.assetOversizeMB", { mb: 5 }),
                            duration: 2,
                            key: "uploading",
                        });
                        this.uploading = false;
                        this.selectedImage = null;
                        this.selectedImageSrc = null;
                        this.hovered = false;
                    }
                } else {
                    this.selectedImage = null;
                    this.selectedImageSrc = null;
                    this.uploading = false;
                    this.hovered = false;
                }
            };
            fileEl.click();
        },
        removeImage(e) {
            e.preventDefault();
            e.stopPropagation();
            this.onUpdated({
                assetId: null,
                imageUrl: null,
            });
        },
        onModeChanged(isDynamic) {
            // Update current mode based on switch state
            this.currentMode = isDynamic ? 'dynamic' : 'static';
            this.updateMode();
        },
        setMode(mode) {
            this.currentMode = mode;
            this.updateMode();
        },
        updateMode() {
            if (this.currentMode === "static") {
                // Switching to static mode
                this.onUpdated({
                    assetId: this.defaultAssetId,
                    imageUrl: this.defaultImageUrl,
                    mode: "static",
                });
            } else {
                // Switching to dynamic mode
                this.onUpdated({
                    assetId: null,
                    imageUrl: this.dynamicImageUrl,
                    mode: "dynamic",
                });
            }
        },
    },
    watch: {
        defaultImageUrl(val) {
            log("defaultImageUrl changed", val);
        },
        dynamicImageUrl(val) {
            if (this.currentMode === "dynamic") {
                this.onUpdated({
                    assetId: null,
                    imageUrl: val,
                    mode: "dynamic",
                });
            }
        },
    },
};
</script>

<style scoped lang="scss">
.image-bot-response-container {
    .mode-selector {
        margin-bottom: 1.5rem;
        
        .mode-switch-container {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 1.5rem;
            padding: 1rem 1.5rem;
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            border-radius: 16px;
            border: 1px solid #dee2e6;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
            transition: all 0.3s ease;
            
            &:hover {
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                transform: translateY(-1px);
            }
            
            .mode-label {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                font-weight: 600;
                font-size: 14px;
                color: #6c757d;
                transition: all 0.3s ease;
                cursor: pointer;
                padding: 0.25rem 0.5rem;
                border-radius: 8px;
                
                .material-icons {
                    font-size: 20px;
                    transition: all 0.3s ease;
                }
                
                &:hover {
                    background: rgba(0, 108, 255, 0.05);
                    color: #006cff;
                }
                
                &.active {
                    color: #006cff;
                    background: rgba(0, 108, 255, 0.1);
                    transform: scale(1.05);
                    
                    .material-icons {
                        transform: rotate(5deg);
                    }
                }
            }
            
            .mode-switch {
                transform: scale(1.2);
                
                &.ant-switch-checked {
                    background-color: #006cff;
                }
                
                &:hover {
                    box-shadow: 0 0 0 2px rgba(0, 108, 255, 0.2);
                }
            }
        }
    }

    .dynamic-image-url {
        .url-label {
            display: block;
            margin-bottom: 0.5rem;
            font-weight: 500;
            color: #333;
        }
    }
}

.image-bot-response {
    max-width: 100%;
    width: 325px;
    height: 260px;
    border-radius: 8px;
    background: #f0f4f7;
    display: flex;
    justify-content: center;
    align-items: center;

    .placeholder {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 100%;
    }

    img.preview {
        width: 325px;
        height: 260px;
        object-fit: cover;
    }

    .uploading-banner {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(240, 244, 247, 0.99);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
    }

    .update-banner {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 108, 255, 0.6);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        cursor: pointer;

        span {
            color: white;

            &.remove {
                text-decoration: underline;
            }
        }
    }

    &.empty {
        &:hover {
            cursor: pointer;
            background-color: #006cff;
            opacity: 0.8;
            color: white;

            .placeholder {
                color: white;
            }
        }
    }
}
</style>
